// ============================================================================
// Theme manager (spec §14.3). Resolves light / dark / system to a concrete
// `data-theme` attribute on <html>, honors prefers-color-scheme for "system"
// and updates live when the OS theme changes. The chosen preference persists
// locally and is synced to the profile (theme_preference) when signed in.
// ============================================================================

import { THEME } from '@core/models/constants.js';

const STORAGE_KEY = 'hanja.theme';
const media = window.matchMedia('(prefers-color-scheme: dark)');
let current = THEME.SYSTEM;
const listeners = new Set();

function systemIsDark() {
  return media.matches;
}

/** The concrete theme actually applied right now: 'light' | 'dark'. */
export function resolvedTheme() {
  if (current === THEME.SYSTEM) return systemIsDark() ? THEME.DARK : THEME.LIGHT;
  return current;
}

function apply() {
  const resolved = resolvedTheme();
  document.documentElement.setAttribute('data-theme', resolved);
  // Keep the browser UI (address bar) in sync.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const style = getComputedStyle(document.documentElement);
    meta.setAttribute('content', style.getPropertyValue('--bg').trim() || '#ffffff');
  }
  listeners.forEach((fn) => fn(current, resolved));
}

/** Read a CSS design token value at runtime (for canvases/charts, §14.3). */
export function token(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function getPreference() {
  return current;
}

export function setPreference(pref) {
  current = Object.values(THEME).includes(pref) ? pref : THEME.SYSTEM;
  try {
    localStorage.setItem(STORAGE_KEY, current);
  } catch {
    /* private mode / storage disabled — non-fatal */
  }
  apply();
  return current;
}

/** Subscribe to theme changes; returns an unsubscribe function. */
export function onThemeChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Cycle the visible control: light → dark → system → light. */
export function cyclePreference() {
  const order = [THEME.LIGHT, THEME.DARK, THEME.SYSTEM];
  const next = order[(order.indexOf(current) + 1) % order.length];
  return setPreference(next);
}

/** Initialize from stored preference (call once at boot). */
export function initTheme() {
  let stored = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  current = Object.values(THEME).includes(stored) ? stored : THEME.SYSTEM;
  // Live-update when the OS theme flips and we're following the system.
  media.addEventListener('change', () => {
    if (current === THEME.SYSTEM) apply();
  });
  apply();
  return current;
}
